import React, { useEffect, useRef, useState } from "react";
import { Modal } from "@fluentui/react";
import { useUser } from "@clerk/clerk-react";
import styles from "./UpgradePlanModal.module.css";

interface UpgradePlanModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    onUpgrade: () => void;
    queriesUsed: number;
    queriesLimit: number;
    showLimitReached?: boolean;
}

type WaitlistState = "idle" | "loading" | "success" | "already_joined" | "error";

export const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({
    isOpen,
    onDismiss,
    queriesUsed,
    queriesLimit,
    showLimitReached = false
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const { user } = useUser();
    const [waitlistState, setWaitlistState] = useState<WaitlistState>("idle");
    const [errorMessage, setErrorMessage] = useState<string>("");

    // Check waitlist status when modal opens
    useEffect(() => {
        const checkWaitlistStatus = async () => {
            if (!isOpen || !user?.id) return;

            try {
                const response = await fetch('/api/subscription/waitlist', {
                    headers: {
                        'x-clerk-user-id': user.id
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.is_on_waitlist) {
                        setWaitlistState("already_joined");
                    }
                }
            } catch (error) {
                console.error("Error checking waitlist status:", error);
            }
        };

        checkWaitlistStatus();
    }, [isOpen, user?.id]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            // Don't reset if already joined
            if (waitlistState !== "already_joined" && waitlistState !== "success") {
                setWaitlistState("idle");
            }
            setErrorMessage("");
        }
    }, [isOpen, waitlistState]);

    // Handle ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onDismiss();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen, onDismiss]);

    if (!isOpen) return null;

    const handleWaitlistClick = async () => {
        if (!user?.id) {
            setErrorMessage("Debes iniciar sesion para unirte a la lista");
            setWaitlistState("error");
            return;
        }

        setWaitlistState("loading");
        setErrorMessage("");

        try {
            const response = await fetch('/api/subscription/waitlist', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-clerk-user-id': user.id
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.already_joined) {
                    setWaitlistState("already_joined");
                } else {
                    setWaitlistState("success");
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                setErrorMessage(errorData.detail || "Error al unirse a la lista");
                setWaitlistState("error");
            }
        } catch (error) {
            console.error("Error joining waitlist:", error);
            setErrorMessage("Error de conexion. Intenta de nuevo.");
            setWaitlistState("error");
        }
    };

    const isJoined = waitlistState === "success" || waitlistState === "already_joined";

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            isBlocking={showLimitReached}
            containerClassName={styles.modalContainer}
            styles={{
                main: {
                    backgroundColor: 'transparent',
                    borderRadius: '20px',
                    border: 'none',
                    maxWidth: '520px',
                    width: '92vw',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 0,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)'
                },
                scrollableContent: {
                    overflow: 'hidden'
                }
            }}
        >
            <div
                ref={modalRef}
                className={styles.modalContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby="upgrade-modal-title"
            >
                {/* Close button */}
                <button
                    className={styles.closeButton}
                    onClick={onDismiss}
                    aria-label="Cerrar"
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Coming Soon Badge */}
                <div className={styles.comingSoonBadge}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L13.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L10.91 8.26L12 2Z" fill="currentColor"/>
                    </svg>
                    <span>Proximamente</span>
                </div>

                {/* Header */}
                <div className={styles.header}>
                    {showLimitReached ? (
                        <>
                            <div className={styles.limitIcon}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="12" cy="16" r="1" fill="currentColor"/>
                                </svg>
                            </div>
                            <h2 id="upgrade-modal-title" className={styles.title}>
                                Limite diario alcanzado
                            </h2>
                            <p className={styles.subtitle}>
                                Has usado tus {queriesLimit} consultas gratuitas de hoy
                            </p>
                        </>
                    ) : (
                        <>
                            <div className={styles.proIcon}>
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.5 16.5L12 3L19.5 16.5H4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M12 16.5V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M8 21H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    <circle cx="12" cy="10" r="2" fill="currentColor"/>
                                </svg>
                            </div>
                            <h2 id="upgrade-modal-title" className={styles.title}>
                                Videntis Pro
                            </h2>
                            <p className={styles.subtitle}>
                                Tu aliado definitivo para comprar auto con confianza
                            </p>
                        </>
                    )}
                </div>

                {/* Quota indicator */}
                {showLimitReached && (
                    <div className={styles.quotaBar}>
                        <div className={styles.quotaProgress} style={{ width: '100%' }} />
                        <span className={styles.quotaText}>{queriesUsed} / {queriesLimit} consultas usadas</span>
                    </div>
                )}

                {/* Value Proposition Box */}
                <div className={styles.valueBox}>
                    <div className={styles.valueIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <div className={styles.valueText}>
                        <strong>Acceso por 30 dias</strong>
                        <span>Tiempo suficiente para investigar, comparar y comprar tu proximo auto sin presiones. Pago unico, sin suscripcion.</span>
                    </div>
                </div>

                {/* Waitlist CTA */}
                <div className={styles.priceSection}>
                    <div className={styles.priceBadge}>Acceso anticipado</div>
                    <div className={styles.priceRow}>
                        <span className={styles.waitlistTitle}>Unete a la lista de espera</span>
                    </div>
                    <span className={styles.priceNote}>Se el primero en conocer el precio y acceder al lanzamiento</span>
                </div>

                {/* Features Section */}
                <div className={styles.featuresContainer}>
                    <div className={styles.featuresSection}>
                        <div className={styles.sectionLabel}>Beneficios incluidos</div>
                        <div className={styles.features}>
                            <div className={styles.featureItem}>
                                <div className={styles.featureIcon}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M21 21L16.5 16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M11 8V14M8 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <span>Busqueda avanzada</span>
                            </div>
                            <div className={styles.featureItem}>
                                <div className={styles.featureIcon}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <span>Soporte prioritario</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.featuresSection}>
                        <div className={`${styles.sectionLabel} ${styles.exclusiveLabel}`}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                            </svg>
                            Funciones exclusivas
                        </div>
                        <div className={styles.features}>
                            <div className={`${styles.featureItem} ${styles.exclusiveFeature}`}>
                                <div className={`${styles.featureIcon} ${styles.exclusiveIcon}`}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M4 9h16M9 4v16" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                </div>
                                <div className={styles.featureContent}>
                                    <span className={styles.featureTitle}>Consultas ilimitadas</span>
                                    <span className={styles.featureDesc}>Sin limite de preguntas durante 30 dias</span>
                                </div>
                            </div>
                            <div className={`${styles.featureItem} ${styles.exclusiveFeature}`}>
                                <div className={`${styles.featureIcon} ${styles.exclusiveIcon}`}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <div className={styles.featureContent}>
                                    <span className={styles.featureTitle}>Vehiculos certificados</span>
                                    <span className={styles.featureDesc}>Revision completa del historial del vehiculo</span>
                                </div>
                            </div>
                            <div className={`${styles.featureItem} ${styles.exclusiveFeature}`}>
                                <div className={`${styles.featureIcon} ${styles.exclusiveIcon}`}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 21h18M5 21V7l8-4 8 4v14" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M9 21v-6h6v6M9 9h.01M15 9h.01M9 13h.01M15 13h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <div className={styles.featureContent}>
                                    <span className={styles.featureTitle}>Talleres y Mecanicos</span>
                                    <span className={styles.featureDesc}>Directorio de profesionales certificados</span>
                                </div>
                            </div>
                            <div className={`${styles.featureItem} ${styles.highlightFeature}`}>
                                <div className={`${styles.featureIcon} ${styles.alertIcon}`}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <div className={styles.featureContent}>
                                    <span className={styles.featureTitle}>Alerta de Fraude</span>
                                    <span className={styles.featureDesc}>Detecta estafas y anuncios sospechosos</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Buttons */}
                <div className={styles.buttonContainer}>
                    {isJoined ? (
                        <>
                            <div className={styles.successMessage}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M8 12l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>
                                    {waitlistState === "already_joined"
                                        ? "Ya estas en la lista de espera"
                                        : "Te has unido a la lista de espera"}
                                </span>
                            </div>
                            <p className={styles.waitlistNote}>
                                Te avisaremos cuando Videntis Pro este disponible
                            </p>
                        </>
                    ) : (
                        <>
                            <button
                                className={styles.waitlistButton}
                                onClick={handleWaitlistClick}
                                disabled={waitlistState === "loading"}
                                aria-label="Unirme a la lista de espera"
                            >
                                {waitlistState === "loading" ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        <span className={styles.buttonText}>Procesando...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className={styles.buttonIconLeft} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2"/>
                                            <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        <span className={styles.buttonText}>Unirme a la Lista de Espera</span>
                                    </>
                                )}
                            </button>
                            <p className={styles.waitlistNote}>
                                Se el primero en acceder cuando lancemos
                            </p>
                        </>
                    )}

                    {waitlistState === "error" && (
                        <p className={styles.errorMessage}>{errorMessage}</p>
                    )}

                    {!showLimitReached && (
                        <button
                            className={styles.laterButton}
                            onClick={onDismiss}
                            aria-label="Mas tarde"
                        >
                            Ahora no, gracias
                        </button>
                    )}

                    {showLimitReached && (
                        <p className={styles.resetNote}>
                            Tu limite gratuito se restablece a medianoche
                        </p>
                    )}
                </div>

                {/* Privacy note */}
                <div className={styles.privacyNote}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>Tu informacion esta segura. Solo te contactaremos para el lanzamiento.</span>
                </div>
            </div>
        </Modal>
    );
};

export default UpgradePlanModal;
